/**
 * Push Push — the answer the shopper gets after pressing "Add to cart".
 *
 * WHAT WAS MEASURED FIRST (live theme, product page, 26/07/2026). One press, instrumented:
 *   t=16ms    POST /cart/add.js sent
 *   t=20ms    Horizon writes data-added="true" on the button (its tick, drawn on hope)
 *   t=1138ms  server answers
 *   t=1347ms  cart re-read finishes
 *   after     header count still "6" — unchanged; cart drawer still shut; and data-added was
 *             never cleared, so the tick sat on top of the button label indefinitely.
 * So the press produced: a confirmation that pre-dated the answer by 1.1s, a stuck overlay, a
 * count that never moved, and no drawer. Four separate silences at the same moment — the point
 * in the funnel where a shopper decides the site is broken and either presses again (duplicate
 * line) or leaves.
 *
 * WHAT THIS FILE DOES
 *   a. Rewrites every [data-pp-cart-count] from the cart the server actually returned.
 *   b. Holds the confirmation from the press until AFTER the response, plus a fixed readable
 *      hold — never a guessed duration.
 *   c. Opens the cart drawer once the response is in, so the confirmation is the cart itself.
 *   d. Puts refusals (sold out, stock limit, network) on screen at the bottom of the viewport,
 *      where the thumb that caused them already is.
 *
 * WHY A CUSTOM ELEMENT AND AN ASSET FILE, NOT INLINE SCRIPT
 * The cart drawer and the product form both re-render through the Section Rendering API and are
 * patched in with morph. morph does not re-run inline <script>, so anything inline in those
 * regions runs once and then silently stops being true. A module asset plus a custom element
 * survives every re-render: the element re-upgrades on insertion, and the listeners below are
 * bound to `document`, which morph never replaces.
 *
 * WHAT IT DOES NOT TOUCH
 * The drawer is opened through <theme-drawer>'s own open() — the same call Horizon's
 * cart-drawer.js makes. The native <dialog> mechanism (scroll lock, focus trap) is left alone.
 */

import { StandardEvents } from '@shopify/events';

/** Confirmation stays this long AFTER the server answers — never measured from the click. */
const HOLD_AFTER_RESPONSE_MS = 1400;

/** How long a refusal stays on screen. Long enough to read a sentence twice. */
const TOAST_MS = 6000;

/**
 * Stand-in number inside the pluralised bag label rendered by buy-buttons.liquid. Liquid picks
 * the plural form (it knows each language's rules), this file puts the real number back.
 */
const COUNT_PLACEHOLDER = '999999';

/** Strings live in the locale files and reach this file through <pp-cart-feedback> attributes. */
const strings = {
  adding: '',
  added: '',
  error: '',
  close: '',
  countLabelOne: '',
  countLabelOther: '',
};

let installed = false;

/* ==========================================================================
   (a) The number on the bag
   ========================================================================== */

/** Guards against an older, slower response overwriting a newer count. */
let countToken = 0;

/**
 * The theme's `Theme` object.
 *
 * snippets/scripts.liquid declares it with `const` in a classic script, which puts it in the
 * global LEXICAL scope — it is therefore reachable by bare name from a module but is NOT a
 * property of `window`. Measured: `window.Theme` is undefined on this theme, so reading it that
 * way silently fell back to "/cart" and to a template name that never matched. `typeof` first,
 * because a bare reference to a name that was never declared throws.
 *
 * @returns {any}
 */
const themeGlobal = () => (typeof Theme === 'undefined' ? undefined : Theme);

/** Locale-aware: on a market with a path prefix the cart lives at e.g. /en/cart.js. */
const cartUrl = () => `${themeGlobal()?.routes?.cart_url ?? '/cart'}.js`;

/**
 * Writes an absolute item count onto every bag counter in the page.
 * @param {number} count
 */
function paintCount(count) {
  for (const el of document.querySelectorAll('[data-pp-cart-count]')) {
    el.textContent = String(count);
    el.toggleAttribute('hidden', count === 0);

    /* The counter sits next to a visually-hidden sentence that repeats the same number for
       screen readers. Leaving it stale would make the bag announce a count the page no longer
       shows. Guarded on every step: if the header markup ever changes shape this simply does
       nothing rather than throwing. */
    const template = count === 1 ? strings.countLabelOne : strings.countLabelOther;
    if (!template) continue;
    const label = el.closest('a, button')?.querySelector('.pp-visually-hidden, .visually-hidden');
    if (label) label.textContent = template.replace(COUNT_PLACEHOLDER, String(count));
  }
}

/**
 * Reads the authoritative count.
 *
 * `cart.totalQuantity` is the whole cart. `detail.itemCount` is NOT — on the add path it is the
 * quantity that was just requested, so using it as a fallback (as Horizon's cart-icon.js does)
 * would print "1" on a cart of six. When the event carries no cart, ask the cart.
 *
 * @param {{cart?: {totalQuantity?: number}} | undefined} result
 * @returns {Promise<number | null>}
 */
async function resolveCount(result) {
  const fromEvent = result?.cart?.totalQuantity;
  if (typeof fromEvent === 'number') return fromEvent;

  try {
    const response = await fetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    const cart = await response.json();
    return typeof cart?.item_count === 'number' ? cart.item_count : null;
  } catch {
    return null;
  }
}

/**
 * @param {{cart?: {totalQuantity?: number}} | undefined} result
 */
async function syncCount(result) {
  const token = ++countToken;
  const count = await resolveCount(result);
  // A newer update landed while this one was resolving — that one is the truth, not this.
  if (count === null || token !== countToken) return;
  paintCount(count);
}

/* ==========================================================================
   (b) The confirmation on the button
   ========================================================================== */

/** Buttons currently owned by this file, mapped to their optional release timer. */
const owned = new Map();

/**
 * Every add-to-cart button that represents the press being answered.
 * The sticky buy bar is included on purpose: on a phone that is the button under the thumb, and
 * it drives the same form, so it must show the same state.
 *
 * @param {EventTarget | null} target
 * @returns {HTMLElement[]}
 */
function buttonsFor(target) {
  const scope =
    (target instanceof Element && (target.closest('product-form-component') ?? target.closest('quick-add-component'))) ||
    document;
  const productId = scope instanceof HTMLElement ? scope.dataset.productId : undefined;
  const matchingStickyButtons = productId
    ? [...document.querySelectorAll('sticky-add-to-cart')]
        .filter((sticky) => sticky instanceof HTMLElement && sticky.dataset.productId === productId)
        .flatMap((sticky) => [...sticky.querySelectorAll('.add-to-cart-button')])
    : [];

  return [
    ...new Set([
      ...scope.querySelectorAll('.add-to-cart-button'),
      ...matchingStickyButtons,
    ]),
  ];
}

/**
 * @param {HTMLElement} button
 * @param {'pending' | 'added' | 'error'} state
 * @param {string} label
 */
function setButtonState(button, state, label) {
  button.setAttribute('data-pp-atc', state);
  button.setAttribute('data-pp-atc-label', label);
  button.toggleAttribute('aria-busy', state === 'pending');

  /* Horizon paints its own tick from the click handler and, on this theme, never removes it —
     the overlay was still sitting on the button label minutes later. While this file owns the
     button it owns the whole surface, so that attribute goes and stays gone. */
  button.removeAttribute('data-added');
}

/**
 * @param {HTMLElement} button
 */
function releaseButton(button) {
  const timer = owned.get(button);
  if (timer) clearTimeout(timer);
  owned.delete(button);
  button.removeAttribute('data-pp-atc');
  button.removeAttribute('data-pp-atc-label');
  button.removeAttribute('aria-busy');
  button.removeAttribute('data-added');
}

/**
 * @param {HTMLElement[]} buttons
 * @param {'pending' | 'added' | 'error'} state
 * @param {string} label
 * @param {number} [releaseAfterMs]
 */
function markButtons(buttons, state, label, releaseAfterMs) {
  for (const button of buttons) {
    const previous = owned.get(button);
    if (previous) clearTimeout(previous);

    setButtonState(button, state, label);
    owned.set(button, releaseAfterMs ? setTimeout(() => releaseButton(button), releaseAfterMs) : undefined);
  }
}

/** @param {HTMLElement[]} buttons */
function playConfirmedFlyToCart(buttons) {
  const components = new Set(
    buttons.map((button) => button.closest('add-to-cart-component')).filter((component) => component)
  );
  for (const component of components) component.playConfirmedFlyToCart?.();
}

/* ==========================================================================
   (c) The drawer
   ========================================================================== */

/**
 * Opens the cart drawer once the add has actually succeeded.
 *
 * Yields entirely when `cart-drawer-component[auto-open]` is present: that is the merchant
 * setting doing the same job through Horizon's own path, and two openers would fight over the
 * focus restore. As shipped the setting is off, which is why the drawer never opened.
 *
 * @param {Element | null} sourceModal - a modal dialog the press came from, if any.
 */
async function openCartDrawer(sourceModal) {
  if (themeGlobal()?.template?.name === 'cart') return;
  if (document.querySelector('cart-drawer-component[auto-open]')) return;

  const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
  if (!drawer) return;

  await customElements.whenDefined('theme-drawer');
  if (drawer.isOpen) return;

  /* Quick-add opens its own modal dialog. Opening ours before that one closes would capture its
     focus-restore target and send focus to the wrong place on close. */
  if (sourceModal instanceof HTMLDialogElement && sourceModal.open) {
    sourceModal.addEventListener('close', () => drawer.open(), { once: true });
    return;
  }

  drawer.open();
}

/* ==========================================================================
   (d) The message strip
   ========================================================================== */

/** @type {HTMLElement | null} */
let toast = null;
/** @type {number | undefined} */
let toastTimer;

function buildToast() {
  if (toast) return toast;

  toast = document.createElement('div');
  toast.className = 'pp-cart-toast';
  toast.setAttribute('role', 'alert');
  toast.hidden = true;

  /* Manual popover, so the strip joins the browser's top layer.
     Measured: with the cart drawer open, its modal <dialog> is in the top layer and paints over
     everything below it whatever the z-index — a refusal raised from inside the open drawer
     (pressing + past the last unit in stock) would land behind it and be invisible again, which
     is the exact bug being fixed. `manual` rather than `auto` so a light-dismiss elsewhere on the
     page cannot silently swallow the message. */
  toast.setAttribute('popover', 'manual');

  const text = document.createElement('p');
  text.className = 'pp-cart-toast__text';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'pp-cart-toast__close';
  close.setAttribute('aria-label', strings.close);
  close.textContent = '×';
  close.addEventListener('click', hideToast);

  toast.append(text, close);
  document.body.appendChild(toast);
  return toast;
}

function hideToast() {
  clearTimeout(toastTimer);
  if (!toast) return;
  // Older browsers without the popover API fall back to [hidden] alone, which still works —
  // it only loses the ability to sit above an open modal drawer.
  try {
    if (toast.matches(':popover-open')) toast.hidePopover();
  } catch {
    /* popover unsupported */
  }
  toast.hidden = true;
}

/**
 * Lifts the strip clear of the sticky buy bar so it never covers the button that produced it.
 * Measured rather than assumed: the bar's height changes with the product title and is 0 on
 * desktop.
 */
function toastOffset() {
  const bar = document.querySelector('sticky-add-to-cart');
  if (!bar) return 0;
  const rect = bar.getBoundingClientRect();
  if (rect.height === 0) return 0;
  // How much of the viewport bottom the bar currently occupies.
  return Math.max(0, Math.min(rect.height, window.innerHeight - rect.top));
}

/**
 * @param {string} message
 * @param {'error' | 'success'} tone
 * @param {boolean} [persistent]
 */
function showToast(message, tone, persistent = false) {
  if (!message) return;

  const element = buildToast();
  const text = element.querySelector('.pp-cart-toast__text');
  if (text) text.textContent = message;

  element.setAttribute('data-pp-tone', tone);
  element.style.setProperty('--pp-toast-offset', `${toastOffset()}px`);
  element.hidden = false;
  try {
    if (!element.matches(':popover-open')) element.showPopover();
  } catch {
    /* popover unsupported — [hidden] above already revealed it */
  }

  clearTimeout(toastTimer);
  toastTimer = persistent ? undefined : setTimeout(hideToast, TOAST_MS);
}

/**
 * The refusal the shopper should read. Use the event that belongs to this Cart API request; a DOM
 * query can pick up an older form's error, and rewriting arbitrary messages based on their first
 * number can turn client-side validation into a false "added" confirmation.
 *
 * @param {string | undefined} eventMessage
 * @returns {string}
 */
function refusalMessage(eventMessage) {
  return eventMessage?.trim() || strings.error;
}

/* ==========================================================================
   Wiring
   ========================================================================== */

function install() {
  if (installed) return;
  installed = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (/** @type {any} */ event) => {
    const isAdd = event.action === 'add';
    const buttons = isAdd ? buttonsFor(event.target) : [];
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    if (isAdd && buttons.length) {
      // ProductForm owns a real request timeout. Keeping this visual state on the same promise
      // prevents the button from looking available while the underlying cart lock is still held.
      markButtons(buttons, 'pending', strings.adding);
    }

    Promise.resolve(event.promise)
      .then((result) => {
        syncCount(result);

        if (!isAdd) return;

        if (result?.detail?.didError) {
          markButtons(buttons, 'error', strings.error, TOAST_MS);
          return;
        }

        // Hold measured from HERE — the moment the server answered — not from the click.
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        markButtons(buttons, 'added', strings.added, HOLD_AFTER_RESPONSE_MS);
        // A modal drawer occupies the browser top layer and would cover a body-level flight.
        // Prefer the immediately useful drawer; keep the flight only on pages without one.
        if (drawer) openCartDrawer(sourceModal);
        // Cart-page mode has no drawer to open, so the strip carries the confirmation instead.
        else {
          playConfirmedFlyToCart(buttons);
          showToast(strings.added, 'success');
        }
      })
      .catch((error) => {
        if (error?.name === 'CartStateUncertainError') {
          markButtons([...document.querySelectorAll('.add-to-cart-button')], 'error', strings.error);
        } else if (isAdd && buttons.length) {
          markButtons(buttons, 'error', strings.error, TOAST_MS);
        }
        syncCount(undefined);
      });
  });

  document.addEventListener(StandardEvents.cartError, (/** @type {any} */ event) => {
    const uncertain = event.code === 'CART_STATE_UNCERTAIN';
    if (uncertain) markButtons([...document.querySelectorAll('.add-to-cart-button')], 'error', strings.error);
    showToast(refusalMessage(event.error), 'error', uncertain);
  });

  // Back/forward cache restores a page with a count that may be stale by several adds.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncCount(undefined);
  });
}

/**
 * Carries the locale strings in from Liquid and installs the listeners once.
 *
 * Kept as an element rather than a bare script so it re-upgrades if morph ever re-inserts the
 * region it lives in; `install()` is idempotent, so several of these on one page are harmless.
 */
class PpCartFeedback extends HTMLElement {
  connectedCallback() {
    const data = this.dataset;
    strings.adding = data.addingText || strings.adding;
    strings.added = data.addedText || strings.added;
    strings.error = data.errorText || strings.error;
    strings.close = data.closeText || strings.close;
    strings.countLabelOne = data.countLabelOne || strings.countLabelOne;
    strings.countLabelOther = data.countLabelOther || strings.countLabelOther;

    install();
  }
}

if (!customElements.get('pp-cart-feedback')) {
  customElements.define('pp-cart-feedback', PpCartFeedback);
}
